import Button from '../marketing/Button';

const SUMMARY_LABELS = {
  parties: 'Parties',
  deliverables: 'Deliverables',
  compensation: 'Compensation',
  usage_rights: 'Usage rights',
  exclusivity: 'Exclusivity',
  timeline_term: 'Timeline / term',
  termination_cancellation: 'Termination / cancellation',
  other_notable_clauses: 'Other notable clauses',
};

const SEVERITY_STYLES = {
  low: 'bg-emerald-50 text-emerald-900 border-emerald-200',
  medium: 'bg-amber-50 text-amber-900 border-amber-200',
  high: 'bg-red-50 text-red-900 border-red-200',
};

const REWRITE_PREVIEW_COUNT = 2;

const RISKY_PHRASE_HINTS = [
  'sole discretion',
  'in perpetuity',
  'irrevocable',
  'without limitation',
  'exclusive worldwide',
  'at its sole',
  'at the sole',
  'terminate at any time',
  'no compensation',
  'royalty-free',
  'assignable without',
];

function inferHighlightFromExcerpt(excerpt) {
  if (!excerpt || excerpt.length < 12) return null;
  const lower = excerpt.toLowerCase();
  for (const phrase of RISKY_PHRASE_HINTS) {
    const i = lower.indexOf(phrase);
    if (i !== -1) return excerpt.slice(i, i + phrase.length);
  }
  return null;
}

function ExcerptWithHighlight({ excerpt, highlightPhrase }) {
  const phrase = (highlightPhrase && String(highlightPhrase).trim()) || inferHighlightFromExcerpt(excerpt);
  if (!excerpt) return null;
  if (!phrase) {
    return <p className="text-base text-gray-800 leading-relaxed font-dm-sans break-words">{excerpt}</p>;
  }
  const lowerEx = excerpt.toLowerCase();
  const lowerPh = phrase.toLowerCase();
  const nodes = [];
  let start = 0;
  let i = lowerEx.indexOf(lowerPh, start);
  let key = 0;
  if (i === -1) {
    return <p className="text-base text-gray-800 leading-relaxed font-dm-sans break-words">{excerpt}</p>;
  }
  while (i !== -1) {
    if (i > start) {
      nodes.push(
        <span key={key++} className="font-dm-sans">
          {excerpt.slice(start, i)}
        </span>
      );
    }
    const matchedLen = phrase.length;
    nodes.push(
      <mark
        key={key++}
        className="rounded-sm bg-amber-100/95 text-gray-900 px-1 py-0.5 font-dm-sans not-italic font-normal"
      >
        {excerpt.slice(i, i + matchedLen)}
      </mark>
    );
    start = i + matchedLen;
    i = lowerEx.indexOf(lowerPh, start);
  }
  if (start < excerpt.length) {
    nodes.push(
      <span key={key++} className="font-dm-sans">
        {excerpt.slice(start)}
      </span>
    );
  }
  return <p className="text-base text-gray-800 leading-relaxed break-words">{nodes}</p>;
}

function RewriteClauseCard({ item }) {
  return (
    <li className="rounded-2xl border border-gray-100 bg-white p-5 sm:p-6 shadow-sm space-y-5">
      <h3 className="text-lg font-semibold text-dark font-dm-sans leading-snug pr-1">{item.title}</h3>

      <div className="space-y-2">
        <p className="text-sm font-semibold text-gray-800 font-dm-sans">Original clause</p>
        <div className="border-l-[3px] border-primary/25 pl-4 sm:pl-5">
          <ExcerptWithHighlight excerpt={item.original_excerpt} highlightPhrase={item.highlight_phrase} />
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-semibold text-gray-800 font-dm-sans">Suggested improvement</p>
        <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-4 sm:px-5 sm:py-5">
          <p className="text-base text-gray-900 leading-relaxed font-dm-sans break-words">{item.suggested_replacement}</p>
        </div>
      </div>

      <div className="space-y-1.5 pt-0.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 font-dm-sans">Why this matters</p>
        <p className="text-sm text-gray-600 leading-relaxed font-dm-sans break-words">{item.why_this_is_better}</p>
      </div>
    </li>
  );
}

function ProfileUsedList({ profileUsed }) {
  if (!profileUsed) return null;
  const rows = [];
  if (profileUsed.primaryPlatform) {
    rows.push({ label: 'Primary platform', value: profileUsed.primaryPlatform });
  }
  if (profileUsed.audienceSize) {
    rows.push({ label: 'Audience size', value: profileUsed.audienceSize });
  }
  if (profileUsed.niche) {
    rows.push({ label: 'Niche', value: profileUsed.niche });
  }
  if (profileUsed.location) {
    rows.push({ label: 'Location', value: profileUsed.location });
  }
  if (profileUsed.creatorBackground) {
    rows.push({ label: 'Creator background', value: profileUsed.creatorBackground });
  }
  if (rows.length === 0) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 font-dm-sans mb-2">Used from your profile</p>
      <ul className="list-disc list-inside text-sm text-slate-700 space-y-1.5 font-dm-sans leading-relaxed">
        {rows.map((row) => (
          <li key={row.label}>
            <span className="font-medium text-slate-800">{row.label}:</span> {row.value}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * @param {'public' | 'creator'} rewriteExperience Public marketing gate vs logged-in creator (full draft, no signup wall).
 */
export default function ContractCheckerSuccessView({
  result,
  rewriteOpen,
  rewriteLoading,
  rewriteError,
  rewriteData,
  generateSaferRevision,
  variant,
  rewriteExperience = 'public',
  rewriteSignupHref = '/signup',
}) {
  const showStandard = variant === 'public';
  const showPersonalized = variant === 'personalized';
  const isCreatorRewrite = rewriteExperience === 'creator';
  const showPublicGate = !isCreatorRewrite;

  const personalizedDraft =
    isCreatorRewrite &&
    rewriteData &&
    typeof rewriteData.full_rewritten_contract === 'string' &&
    rewriteData.full_rewritten_contract.trim().length > 0;

  return (
    <>
      {showPersonalized && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left">
          <p className="text-sm font-semibold text-slate-800 font-dm-sans">Personalized with your InfluenceHub profile</p>
          <p className="text-xs text-slate-600 mt-1 leading-relaxed font-dm-sans">
            Based on available profile details like platform, audience size, niche, and creator background.
          </p>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6">
        {showStandard && (
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3 font-dm-sans">Standard analysis</p>
        )}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div>
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-1">Overall score</p>
            <p className="text-4xl sm:text-5xl font-bold text-dark font-dm-sans">
              {result.score}
              <span className="text-xl text-gray-400 font-normal"> / 10</span>
            </p>
          </div>
          <div className="sm:text-right">
            <span
              className={`inline-block px-3 py-1 rounded-full text-sm font-semibold capitalize border ${
                result.risk_level === 'low'
                  ? 'bg-emerald-50 text-emerald-900 border-emerald-200'
                  : result.risk_level === 'moderate'
                    ? 'bg-amber-50 text-amber-900 border-amber-200'
                    : 'bg-red-50 text-red-900 border-red-200'
              }`}
            >
              {result.risk_level} risk
            </span>
          </div>
        </div>
        <p className="text-gray-700 leading-relaxed text-base border-t border-gray-100 pt-4">{result.quick_verdict}</p>
      </div>

      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6">
        <h2 className="text-lg font-semibold text-dark mb-4 font-dm-sans">Contract summary</h2>
        <ul className="space-y-4">
          {Object.entries(SUMMARY_LABELS).map(([key, label]) => {
            const items = (result.summary && result.summary[key]) || [];
            return (
              <li key={key}>
                <h3 className="text-sm font-semibold text-gray-800 mb-1">{label}</h3>
                {items.length === 0 ? (
                  <p className="text-sm text-gray-500">Not specified in the pasted text.</p>
                ) : (
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                    {items.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {result.red_flags && result.red_flags.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6">
          <h2 className="text-lg font-semibold text-dark mb-4 font-dm-sans">Red flags to review</h2>
          <ul className="space-y-4">
            {result.red_flags.map((flag, i) => (
              <li
                key={i}
                className={`rounded-xl border p-4 ${SEVERITY_STYLES[flag.severity] || SEVERITY_STYLES.medium}`}
              >
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="font-semibold text-sm">{flag.title}</span>
                  <span className="text-xs uppercase opacity-80">({flag.severity})</span>
                </div>
                {flag.evidence_quote ? (
                  <p className="text-sm font-mono bg-white/60 rounded-lg px-2 py-1 mb-2 break-words">&ldquo;{flag.evidence_quote}&rdquo;</p>
                ) : null}
                <p className="text-sm mb-1 leading-relaxed">{flag.explanation}</p>
                <p className="text-sm font-medium opacity-90 leading-relaxed">Why it matters: {flag.why_it_matters}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6">
        <h2 className="text-lg font-semibold text-dark mb-4 font-dm-sans">Questions to ask the brand</h2>
        <ol className="list-decimal list-inside space-y-2 text-gray-800 text-sm sm:text-base leading-relaxed">
          {(result.questions_for_brand || []).map((q, i) => (
            <li key={i} className="pl-1">
              {q}
            </li>
          ))}
        </ol>
      </div>

      {!rewriteData && !rewriteOpen && (
        <div className="flex flex-col items-center gap-2">
          <Button
            type="button"
            size="lg"
            variant="secondary"
            className="w-full sm:w-auto min-h-[48px]"
            onClick={generateSaferRevision}
          >
            Create a safer version
          </Button>
          <p className="text-xs text-gray-500 text-center max-w-md leading-relaxed">
            Get suggested friendlier wording for the parts of this deal that usually matter most to creators.
          </p>
        </div>
      )}

      {(rewriteOpen || rewriteLoading || rewriteData || rewriteError) && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6 space-y-6">
          {rewriteLoading && (
            <p className="text-sm text-gray-600 py-4 text-center font-dm-sans" aria-live="polite">
              {isCreatorRewrite
                ? 'Drafting your personalized safer draft and clause-by-clause notes…'
                : 'Drafting suggested clause wording for you…'}
            </p>
          )}

          {rewriteError && (
            <div className="rounded-xl bg-red-50 border border-red-200 text-red-900 text-sm p-4 space-y-2" role="alert">
              <p>{rewriteError}</p>
              {!rewriteData ? (
                <Button type="button" variant="ghost" className="text-sm" onClick={generateSaferRevision} disabled={rewriteLoading}>
                  {rewriteLoading ? 'Trying…' : 'Try again'}
                </Button>
              ) : null}
            </div>
          )}

          {personalizedDraft && (
            <div className="space-y-4 border-b border-gray-100 pb-8">
              <div>
                <h2 className="text-lg font-semibold text-dark font-dm-sans">Safer draft based on your profile</h2>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed font-dm-sans">
                  This version was rewritten using your InfluenceHub profile and the original contract to make the language more tailored to
                  your platform, audience, and creator background. This is not legal advice.
                </p>
              </div>
              <ProfileUsedList profileUsed={rewriteData.profile_used} />
              <div className="rounded-xl border border-gray-200 bg-gray-50/90 px-4 py-4 sm:px-5 sm:py-5">
                <pre className="whitespace-pre-wrap text-sm text-gray-900 leading-relaxed font-dm-sans break-words m-0">
                  {rewriteData.full_rewritten_contract}
                </pre>
              </div>
            </div>
          )}

          {rewriteData && Array.isArray(rewriteData.rewrites) && rewriteData.rewrites.length > 0 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-dark font-dm-sans">Clause-by-clause review</h2>
                <p className="text-sm text-gray-600 mt-1 leading-relaxed font-dm-sans">
                  Ideas you can discuss with the brand, in everyday language. This is not legal advice.
                </p>
              </div>

              <ul className="space-y-4 list-none p-0 m-0">
                {(showPublicGate ? rewriteData.rewrites.slice(0, REWRITE_PREVIEW_COUNT) : rewriteData.rewrites).map((item, i) => (
                  <RewriteClauseCard key={`${item.category_id}-${i}`} item={item} />
                ))}
              </ul>

              {showPublicGate && rewriteData.rewrites.length > REWRITE_PREVIEW_COUNT && (
                <div className="relative rounded-2xl border border-gray-200 overflow-hidden min-h-[200px]">
                  <div className="blur-[6px] pointer-events-none select-none opacity-50 space-y-4 p-1" aria-hidden="true">
                    <ul className="space-y-4 list-none p-0 m-0">
                      {rewriteData.rewrites.slice(REWRITE_PREVIEW_COUNT).map((item, i) => (
                        <RewriteClauseCard key={`locked-${item.category_id}-${i}`} item={item} />
                      ))}
                    </ul>
                  </div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 bg-white/65 backdrop-blur-[2px]">
                    <p className="text-sm text-center text-gray-700 max-w-sm leading-relaxed font-dm-sans">
                      More suggested clauses are hidden until you create an account.
                    </p>
                    <Button href={rewriteSignupHref} size="lg" className="w-full sm:w-auto min-h-[48px] text-center">
                      Create free account to unlock full revision and more creator tools
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
